/**
 * The markup of the poster's size and finish choices : the desktop screens
 * (shared.screens.PosterBaseScreen, "Fermer" top right) and the mobile sheet
 * (shared.popups.PosterOptionsSheet, data.sheet : a handle instead, no close button). The title,
 * the subtitle, a grid of cards — real buttons, aria-pressed on the chosen one, every control a
 * data-action handled by the owner.
 *
 *   cards(list)        [{ id, html, selected }] : the cards
 *   artwork(url)       the customer's poster, in every card
 *   select(id)         the chosen card
 *   focusSelected()
 *   closing()          sheet : slides down (the owner removes it once done)
 *   onPulled           sheet : called when the handle is pulled down far enough to close
 *
 * On the size screen every poster is drawn at one scale (the handoffs' 3 px per cm on desktop,
 * 1.75 in the sheet, less when the cards are too narrow for the biggest size) and stands on one
 * baseline : comparing is one look.
 */
fx.ns("shared.components", class PosterOptionsView extends fx.Component {

    // px per cm : A1 (59 × 84) is 177 × 252 px on desktop ; the baseline's height in the stage
    get SCALE() { return this.data.sheet ? 1.75 : 3; }
    get FLOOR() { return this.data.sheet ? 16 : 28; }

    create()
    {
        super.create();

        this.addClass("po" + (this.data.kind == "finish" ? "Finish" : "Size"));
        if (this.data.sheet)
            this.addClass("poSheetView");
    }

    onReady()
    {
        super.onReady();

        if (window.ResizeObserver)
        {
            this._observer = new ResizeObserver(() => this._fit());
            this._observer.observe(this.node);
        }

        if (this.data.sheet)
            this._listenPull();

        // the sheet rises once it is in place
        requestAnimationFrame(() => requestAnimationFrame(() => this.addClass("poOpen")));
    }

    closing()
    {
        this.removeClass("poOpen");
        this.addClass("poClosing");
        let sheet = this.one(".poSheet");
        if (sheet)
            sheet.style.transform = "";
    }

    // the handle (and the title under it) pulled down : past PULL px the sheet closes, short of it
    // it goes back up
    get PULL() { return 100; }

    _listenPull()
    {
        let sheet = this.one(".poSheet");
        let start = null;

        this._pull = {
            down : (e) => {
                if (!e.target.closest || !e.target.closest("[data-drag]"))
                    return;
                start = { y : e.clientY, id : e.pointerId };
                sheet.classList.add("poDragging");
            },
            move : (e) => {
                if (!start)
                    return;
                let dy = Math.max(0, e.clientY - start.y);
                sheet.style.transform = "translateY(" + dy + "px)";
            },
            up : (e) => {
                if (!start)
                    return;
                let dy = e.clientY - start.y;
                start = null;
                sheet.classList.remove("poDragging");
                if (dy > this.PULL && this.onPulled)
                    this.onPulled();
                else
                    sheet.style.transform = "";
            },
        };

        sheet.addEventListener("pointerdown", this._pull.down);
        window.addEventListener("pointermove", this._pull.move);
        window.addEventListener("pointerup", this._pull.up);
        window.addEventListener("pointercancel", this._pull.up);
    }

    destroy()
    {
        if (this._observer)
            this._observer.disconnect();

        if (this._pull)
        {
            window.removeEventListener("pointermove", this._pull.move);
            window.removeEventListener("pointerup", this._pull.up);
            window.removeEventListener("pointercancel", this._pull.up);
        }

        super.destroy();
    }

    cards(list)
    {
        this._list = list;
        let grid = this.one(".poGrid");
        grid.innerHTML = list.map((card) => card.html).join("");
        grid.dataset.count = list.length;           // fewer than four cards stay centred, at the same width

        if (this._artwork)
            this.artwork(this._artwork);

        this._fit();
    }

    artwork(url)
    {
        this._artwork = url;
        this.findAll(".poArt").forEach((img) => img.src = url);
    }

    select(id)
    {
        this.findAll(".poCard").forEach((card) => card.setAttribute("aria-pressed", card.dataset.id == id));
    }

    focusSelected()
    {
        let card = this.one('.poCard[aria-pressed="true"]') || this.one(".poCard");
        if (card)
            card.focus({ preventScroll : true });
    }

    // the first match only : fx's find answers an array as soon as several elements match
    one(selector)
    {
        return this.node ? this.node.querySelector(selector) : null;
    }

    findAll(selector)
    {
        return this.node ? Array.prototype.slice.call(this.node.querySelectorAll(selector)) : [];
    }

    // one scale for every size : the biggest one fits its stage
    _fit()
    {
        if (this.data.kind == "finish" || !this.node)
            return;

        let stage = this.one(".poStage");
        let posters = this.findAll(".poPoster");
        if (!stage || !posters.length)
            return;

        let widest = Math.max.apply(null, posters.map((p) => Number(p.dataset.w)));
        let tallest = Math.max.apply(null, posters.map((p) => Number(p.dataset.h)));
        if (this._observer && this._observed != stage)
        {
            this._observed = stage;
            this._observer.observe(stage);
        }

        // not laid out yet (the screen still sliding in) : again at the next frame, a second at most
        if (!stage.clientWidth)
        {
            this._tries = (this._tries || 0) + 1;
            if (this._tries < 60)
                requestAnimationFrame(() => this._fit());
            return;
        }
        this._tries = 0;

        let room = { width : stage.clientWidth - 16, height : stage.clientHeight - this.FLOOR - 10 };
        let scale = Math.min(this.SCALE, room.width / widest, room.height / tallest);

        posters.forEach((p) => {
            p.style.width = Math.round(Number(p.dataset.w) * scale) + "px";
            p.style.height = Math.round(Number(p.dataset.h) * scale) + "px";
        });
    }

    render()
    {
        let id = "po-title-" + this.id;
        // French punctuation never wraps alone onto its own line
        let title = String(this.data.title || "").replace(/ ([?!:;])/g, "&nbsp;$1");

        if (this.data.sheet)
            return  '<div class="poBackdrop" data-action="close"></div>' +
                    '<div class="poSheet" role="dialog" aria-modal="true" aria-labelledby="' + id + '">' +
                        // the handle and the title stay put, only the cards scroll under them
                        '<div class="poHeader" data-drag="1">' +
                            '<div class="poHandle"><span></span></div>' +
                            '<h1 class="poTitle" id="' + id + '">' + title + '</h1>' +
                            (this.data.subtitle ? '<p class="poSubtitle">' + this.data.subtitle + '</p>' : '') +
                        '</div>' +
                        '<div class="poBody">' +
                            '<div class="poGrid"></div>' +
                        '</div>' +
                    '</div>';

        return  '<div class="poOverlay" role="dialog" aria-modal="true" aria-labelledby="' + id + '">' +
                    '<div class="poTop">' +
                        '<button type="button" class="poClose" data-action="close">' + fx.text("Fermer") +
                            '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
                        '</button>' +
                    '</div>' +
                    '<div class="poBody">' +
                        '<h1 class="poTitle" id="' + id + '">' + title + '</h1>' +
                        '<p class="poSubtitle">' + this.data.subtitle + '</p>' +
                        '<div class="poGrid"></div>' +
                    '</div>' +
                '</div>';
    }
});
